import * as React from 'react'
import { useIntersectionObserver } from 'react-intersection-observer-hook'
import { useRouter } from 'next/router'
import NextImage from 'next/image'

import { Text } from '@wartek-id/text'

import { useAPIInfoTerkiniList } from 'api/info-terkini/useAPIInfoTerkini'
import { useAPIInfoTerkiniCategories } from 'api/info-terkini/useAPIInfoTerkiniCategories'
import { EmptyState, EmptyStateAction } from '../../components/EmptyState'
import { useOnlineStatus } from 'utils/hooks/useOnlineStatus'
import InfoTerkiniListItemCard from './InfoTerkiniListItemCard'
import FilterSheet from './components/FilterSheet'

const InfoTerkiniListGroup = ({
  isDesktop,
  selectedId,
  onSelectItem,
  scrollableTarget,
}: any) => {
  const isOnline = useOnlineStatus()
  const loadingStateData = new Array(3).fill('')
  const [ref, { entry }] = useIntersectionObserver({
    root: scrollableTarget ? document.getElementById(scrollableTarget) : null,
    rootMargin: '100px',
  })

  const router = useRouter()

  // parse the category ids from router.query
  const { categoryid: categoryIdQuery } = router.query
  const categoryIds = React.useMemo(() => {
    if (typeof categoryIdQuery === 'string') {
      return [categoryIdQuery]
    } else if (Array.isArray(categoryIdQuery)) {
      return categoryIdQuery
    } else {
      return []
    }
  }, [categoryIdQuery])

  const {
    data: dataInfoTerkini,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetched,
    refetch,
  } = useAPIInfoTerkiniList(categoryIds)

  const { data: loadedCategories } = useAPIInfoTerkiniCategories()

  const [selectedCategories, setSelectedCategories] =
    React.useState<string[]>(categoryIds)

  const [isSheetOpen, setIsSheetOpen] = React.useState(false)

  const isAnyCategorySelected = selectedCategories.length > 0

  const isChangingCategories = React.useRef(false)

  const handleCategoryChange = (selectedCategories: string[]) => {
    isChangingCategories.current = true
    setSelectedCategories(selectedCategories)
  }

  React.useEffect(() => {
    if (isChangingCategories.current) {
      isChangingCategories.current = false

      router.push({
        pathname: router.pathname,
        query: {
          categoryid: selectedCategories,
        },
      })
    }
  }, [router, selectedCategories])

  React.useEffect(() => {
    if (entry && entry.isIntersecting) {
      fetchNextPage()
    }
  }, [entry, fetchNextPage])

  return (
    <div>
      <div
        className={
          isDesktop
            ? 'bg-surface-subdued w-full'
            : 'px-4 py-8 bg-surface-subdued w-full absolute overflow-y-scroll'
        }
        style={
          isDesktop
            ? undefined
            : {
                height: 'calc(100vh - 7.5rem)',
              }
        }
      >
        {isOnline ? (
          <>
            <FilterSheet
              isDesktop={isDesktop}
              isSheetOpen={isSheetOpen}
              categories={loadedCategories?.data ?? []}
              selectedCategoryIds={selectedCategories}
              onFilter={handleCategoryChange}
              onSheetStateChange={setIsSheetOpen}
            />

            <div className={isDesktop ? 'pb-8 bg-white h-full' : ''}>
              {/* LOADING STATE */}
              {isLoading &&
                loadingStateData.map((_, idx) => (
                  <InfoTerkiniListItemCard
                    key={idx}
                    data-testid="loading-state"
                    isDesktop={isDesktop}
                  />
                ))}

              {isError && (
                <EmptyState
                  className="mt-8"
                  title="Gagal Memuat Info Terkini"
                  description="Maaf, ada masalah pada sistem kami. Mohon coba lagi."
                >
                  <EmptyStateAction onClick={() => refetch()}>
                    Coba Lagi
                  </EmptyStateAction>
                </EmptyState>
              )}

              {isFetched && (
                <>
                  {dataInfoTerkini?.pages[0].data.length === 0 &&
                    (isAnyCategorySelected ? (
                      <article
                        className="mt-16 flex flex-col justify-center text-sm h-full px-5"
                        data-testid="empty-list-filtered-category"
                      >
                        <div className="flex mb-4">
                          <NextImage
                            src="/images/info-terkini-not-found.svg"
                            alt=""
                            width="240"
                            height="160"
                          />
                        </div>

                        <Text as="h2" variant="heading-md" className="mb-2">
                          Belum ada info di sini
                        </Text>
                        <Text className="pb-4" color="subdued">
                          Cek daftar kategori untuk lihat info lainnya.
                        </Text>

                        <EmptyStateAction
                          color="black"
                          onClick={() => {
                            handleCategoryChange([])
                          }}
                        >
                          Lihat daftar info
                        </EmptyStateAction>
                      </article>
                    ) : (
                      <EmptyState
                        className="mt-16"
                        title="Info Terkini Tidak Ditemukan"
                        description=""
                        data-testid="empty-list"
                      >
                        <EmptyStateAction onClick={() => refetch()}>
                          Coba Lagi
                        </EmptyStateAction>
                      </EmptyState>
                    ))}
                  {dataInfoTerkini?.pages.map((page) =>
                    page.data.map((InfoTerkini, index) => (
                      <InfoTerkiniListItemCard
                        data={InfoTerkini}
                        key={index}
                        isSelected={selectedId === InfoTerkini.id}
                        isDesktop={isDesktop}
                        onClick={() => onSelectItem?.(InfoTerkini.id)}
                      />
                    ))
                  )}
                  {hasNextPage
                    ? loadingStateData.map((_, idx) => (
                        <div ref={ref} key={idx} />
                      ))
                    : !(dataInfoTerkini?.pages[0].data.length === 0) &&
                      !isError && (
                        <div className="flex items-center justify-center mx-4 text-sm text-gray-400 pt-8">
                          Semua info terkini sudah ditampilkan.
                        </div>
                      )}
                </>
              )}
            </div>
          </>
        ) : (
          <EmptyState
            className="mt-8"
            title="Gagal Memuat Info Terkini"
            description="Maaf, ada masalah pada sistem kami. Mohon coba lagi."
            data-testid="error-state"
          >
            <EmptyStateAction onClick={() => refetch()}>
              Coba Lagi
            </EmptyStateAction>
          </EmptyState>
        )}
      </div>
    </div>
  )
}

export default InfoTerkiniListGroup
